/**
 * Filtering and sorting for the account artwork library. Kept pure so the
 * route only decides which rows reach this. A missing sort is newest first.
 */

import type { Product } from "./api.ts";
import { artworkSize, fitsProduct } from "./legacy.ts";

export type ArtworkListKind = "design" | "pdf";
export type ArtworkListSort = "oldest" | "name" | "name-desc" | "updated";
/** Omitted means the customer's own files. */
export type ArtworkListView = "templates";

export interface ArtworkListSearch {
  q?: string;
  kind?: ArtworkListKind;
  size?: string;
  pages?: number;
  /** Omitted means newest first. */
  sort?: ArtworkListSort;
  approval?: boolean;
  view?: ArtworkListView;
}

/** Enough of a library row to filter. Drafts are Chaimera files, including locked ones. */
export interface ArtworkListItem {
  name: string;
  draft: boolean;
  created_at: number;
  updated_at: number;
  width: number | null;
  height: number | null;
  pages: number | null;
}

/** Design until a proof exists, then Locked. An upload is a PDF. */
export type ArtworkFileStatus = "design" | "locked" | "pdf";

export function artworkFileStatus(row: { draft: boolean; locked?: boolean }): ArtworkFileStatus {
  if (!row.draft) return "pdf";
  return row.locked ? "locked" : "design";
}

/** A library file or a campaign snapshot. Snapshots stay out of the list. */
export interface ListedArtwork {
  id: string;
  name: string;
  created_at: number;
  updated_at: number | null;
  design_id?: string | null;
  campaign_id?: string | null;
  width_mm: number | null;
  height_mm: number | null;
  page_count: number | null;
  version?: number | null;
  /** Present on an uploaded file. Absent on a design. */
  content_type?: string | null;
}

export interface ListedDesign {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  width: number | null;
  height: number | null;
  pageCount: number | null;
}

/** One row in the artwork library. A proof PDF is not a row; its design is Locked. */
export interface BuiltLibraryRow {
  id: string;
  name: string;
  created_at: number;
  updated_at: number;
  design_id: string | null;
  width: number | null;
  height: number | null;
  pages: number | null;
  draft: boolean;
  locked: boolean;
  /** Library PDF this design was exported to. Null until it is locked. */
  proofId: string | null;
  version: number | null;
  /** Upload media type. Null on a design. */
  content_type: string | null;
}

/**
 * Keep every design. A library PDF whose design is in this list is that
 * design's proof: hide the PDF and mark the design locked. Uploads stay.
 * Campaign snapshots never appear here.
 */
export function buildLibraryRows(
  files: ListedArtwork[],
  designs: ListedDesign[],
): BuiltLibraryRow[] {
  const library = files.filter((file) => !file.campaign_id);
  const present = new Set(designs.map((design) => design.id));
  const proofByDesign = new Map<string, string>();
  for (const file of library) {
    if (file.design_id && present.has(file.design_id) && !proofByDesign.has(file.design_id)) {
      proofByDesign.set(file.design_id, file.id);
    }
  }
  // A proof whose design is not in this list stays visible as a PDF. Otherwise
  // a slow design query, or a deleted design, would hide the file entirely.
  const uploads: BuiltLibraryRow[] = library
    .filter((file) => !(file.design_id && present.has(file.design_id)))
    .map((file) => ({
      id: file.id,
      name: file.name,
      created_at: file.created_at,
      updated_at: file.updated_at ?? file.created_at,
      design_id: file.design_id ?? null,
      width: file.width_mm,
      height: file.height_mm,
      pages: file.page_count,
      draft: false,
      locked: false,
      proofId: null,
      version: file.version ?? 1,
      content_type: file.content_type ?? null,
    }));
  const designRows: BuiltLibraryRow[] = designs.map((design) => ({
    id: design.id,
    name: design.name,
    created_at: design.createdAt,
    updated_at: design.updatedAt,
    design_id: design.id,
    width: design.width,
    height: design.height,
    pages: design.pageCount,
    draft: true,
    locked: proofByDesign.has(design.id),
    proofId: proofByDesign.get(design.id) ?? null,
    version: null,
    content_type: null,
  }));
  return [...uploads, ...designRows];
}

/**
 * List thumb. A design uses its builder preview, on the address page when it
 * has one. A raster upload is already an image. A PDF has no thumb.
 */
export function libraryRowThumbSrc(
  row: {
    id: string;
    draft: boolean;
    content_type?: string | null;
    mailPanelPage?: number | null;
  },
  designListThumb?: (index: number) => string,
): string | null {
  if (row.draft) return designListThumb?.(row.mailPanelPage ?? 0) ?? null;
  if (row.content_type?.startsWith("image/")) return `/api/artwork/${row.id}/file`;
  return null;
}

export interface ArtworkUsageFile {
  id: string;
  design_id?: string | null;
  campaign_id?: string | null;
  source_artwork_id?: string | null;
}

export interface ArtworkUsageCampaign {
  id: string;
  name: string;
  status: string;
  artwork_id: string | null;
  design_id?: string | null;
  customer_id?: string | null;
  user_id?: string | null;
}

/**
 * Campaigns this library file is attached to. A design matches the campaign's
 * current design. A proof snapshot counts only when the campaign has no
 * design, so an older proof cannot share the campaign with the design it
 * uses now. An upload matches a direct attach or a snapshot copied from it.
 * Deleted campaigns are not usage.
 */
export function campaignsUsingArtwork<T extends ArtworkUsageCampaign>(
  row: { id: string; draft: boolean },
  files: readonly ArtworkUsageFile[],
  campaigns: readonly T[],
): T[] {
  const proofs = new Set(
    files.filter((file) => !file.campaign_id && file.design_id === row.id).map((file) => file.id),
  );
  return campaigns.filter((campaign) => {
    if (campaign.status === "deleted") return false;
    const copy = files.find((file) => file.campaign_id && file.id === campaign.artwork_id);
    if (row.draft) {
      if (campaign.design_id) return campaign.design_id === row.id;
      if (!copy) return false;
      if (copy.design_id === row.id) return true;
      return copy.source_artwork_id != null && proofs.has(copy.source_artwork_id);
    }
    if (campaign.artwork_id === row.id) return true;
    return copy?.source_artwork_id === row.id;
  });
}

/**
 * Second line under a name another row in `rows` shares. One campaign shows
 * its name, several show a count, and none says it is not on a campaign.
 * A unique name returns null.
 */
export function sameNameCampaignLine(
  row: { id: string; name: string; draft: boolean },
  rows: readonly { id: string; name: string }[],
  files: readonly ArtworkUsageFile[],
  campaigns: readonly ArtworkUsageCampaign[],
): string | null {
  if (!rows.some((other) => other.id !== row.id && other.name === row.name)) return null;
  const using = campaignsUsingArtwork(row, files, campaigns);
  if (using.length === 0) return "Not on a campaign";
  const only = using.length === 1 ? using[0] : undefined;
  if (only) return only.name.trim() || "Untitled Campaign";
  return `${using.length} campaigns`;
}

const KINDS = new Set<ArtworkListKind>(["design", "pdf"]);
const SORTS = new Set<ArtworkListSort>(["oldest", "name", "name-desc", "updated"]);
const VIEWS = new Set<ArtworkListView>(["templates"]);

type SizeProduct = Pick<Product, "width_mm" | "height_mm" | "size">;

/**
 * Why a library file cannot be chosen for this product, or null when it can.
 * A file with no measured page is not blocked: inspection may still be running.
 */
export function artworkChoiceBlock(
  product: Pick<Product, "width_mm" | "height_mm" | "size"> | null | undefined,
  width: number | null,
  height: number | null,
): string | null {
  if (!product || !width || !height) return null;
  if (fitsProduct(product, width, height)) return null;
  return `Does not match ${product.size ?? "the product"}`;
}

/** Trim-size label, or null when the file has no measured page. */
export function artworkSizeName(
  products: SizeProduct[],
  width: number | null,
  height: number | null,
): string | null {
  if (!width || !height) return null;
  return artworkSize(products, width, height).name;
}

/** Drops anything that is not a real facet value, so a bad URL shows everything. */
export function parseArtworkListSearch(raw: Record<string, unknown>): ArtworkListSearch {
  const search: ArtworkListSearch = {};
  if (typeof raw.q === "string" && raw.q !== "") search.q = raw.q;
  if (typeof raw.kind === "string" && KINDS.has(raw.kind as ArtworkListKind)) {
    search.kind = raw.kind as ArtworkListKind;
  }
  if (typeof raw.size === "string" && raw.size !== "") search.size = raw.size;
  const pages = typeof raw.pages === "number" ? raw.pages : Number(raw.pages);
  if (Number.isInteger(pages) && pages > 0) search.pages = pages;
  if (typeof raw.sort === "string" && SORTS.has(raw.sort as ArtworkListSort)) {
    search.sort = raw.sort as ArtworkListSort;
  }
  if (raw.approval === true || raw.approval === "true") search.approval = true;
  if (typeof raw.view === "string" && VIEWS.has(raw.view as ArtworkListView)) {
    search.view = raw.view as ArtworkListView;
  }
  return search;
}

export const hasActiveArtworkFilters = (s: ArtworkListSearch): boolean =>
  Boolean(s.q || s.kind || s.size || s.pages || s.sort);

/** Size and page chips from the whole library, so a selection does not hide the others. */
export function artworkFacets(
  rows: ArtworkListItem[],
  products: SizeProduct[],
): { sizes: string[]; pages: number[] } {
  const sizes = [
    ...new Set(
      rows.flatMap((row) => {
        const name = artworkSizeName(products, row.width, row.height);
        return name ? [name] : [];
      }),
    ),
  ].sort((a, b) => a.localeCompare(b));
  const pages = [...new Set(rows.flatMap((row) => (row.pages == null ? [] : [row.pages])))].sort(
    (a, b) => a - b,
  );
  return { sizes, pages };
}

/** Filters combine with AND. Equal sort keys keep their incoming order. */
export function filterArtwork<T extends ArtworkListItem>(
  rows: T[],
  products: SizeProduct[],
  search: ArtworkListSearch,
): T[] {
  const term = (search.q ?? "").trim().toLocaleLowerCase();
  const matched = rows.filter((row) => {
    if (term && !row.name.toLocaleLowerCase().includes(term)) return false;
    if (search.kind === "design" && !row.draft) return false;
    if (search.kind === "pdf" && row.draft) return false;
    if (search.size && artworkSizeName(products, row.width, row.height) !== search.size) {
      return false;
    }
    if (search.pages != null && row.pages !== search.pages) return false;
    return true;
  });
  const cmp = (a: T, b: T): number => {
    switch (search.sort) {
      case "oldest":
        return a.created_at - b.created_at;
      case "name":
        return a.name.localeCompare(b.name);
      case "name-desc":
        return b.name.localeCompare(a.name);
      case "updated":
        return b.updated_at - a.updated_at;
      case undefined:
        return b.created_at - a.created_at;
      default: {
        const unreachable: never = search.sort;
        return unreachable;
      }
    }
  };
  return [...matched].sort(cmp);
}
